import React, { useState, useEffect } from 'react';
import {
  FaChartLine,
  FaChartBar,
  FaChartPie,
  FaUsers,
  FaDollarSign,
  FaCreditCard,
  FaWifi,
  FaCalendar,
  FaDownload,
  FaFilter,
  FaRedo
} from 'react-icons/fa';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { API_ENDPOINTS } from '../../config/api';
import { useAdminAuth } from '../context/AdminAuthContext';
import DataQualityBanner from './DataQualityBanner';
import BasQuarterPanel from './BasQuarterPanel';

const Analytics = () => {
  const { token } = useAdminAuth();
  const [timeRange, setTimeRange] = useState('1y');
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState({
    totalRevenue: 0,
    itServicesRevenue: 0, // NEW
    newCustomers: 0,
    ddSuccessRate: 0,
    activeServices: 0,
    revenueGrowth: 0,
    customerGrowth: 0,
    ddGrowth: 0,
    servicesGrowth: 0,
    history: [],
    itServicesHistory: [], // NEW
    planDistribution: {},
    recentActivity: []
  });

  const COLORS = ['#83a598', '#fabd2f', '#b8bb26', '#d3869b', '#fe8019', '#fb4934'];

  useEffect(() => {
    loadAnalytics();
  }, [timeRange, token]);

  const loadAnalytics = async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);

      const response = await fetch(API_ENDPOINTS.ADMIN_GET_CUSTOMERS, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.stats) {
        const stats = data.stats;

        const revenueGrowth = stats.lastMonthRevenue && stats.lastMonthRevenue > 0 ?
          ((stats.monthlyRevenue - stats.lastMonthRevenue) / stats.lastMonthRevenue * 100).toFixed(1) : 0;

        const customerGrowth = stats.lastMonthCustomers && stats.lastMonthCustomers > 0 ?
          ((stats.total - stats.lastMonthCustomers) / stats.lastMonthCustomers * 100).toFixed(1) : 0;

        const ddGrowth = stats.lastMonthDdRate && stats.lastMonthDdRate > 0 ?
          (stats.ddSuccessRate - stats.lastMonthDdRate).toFixed(1) : 0;

        const servicesGrowth = stats.lastMonthServices && stats.lastMonthServices > 0 ?
          ((stats.activeServices - stats.lastMonthServices) / stats.lastMonthServices * 100).toFixed(1) : 0;

        // Process Plan Distribution for Pie Chart
        const planData = Object.entries(stats.planDistribution || {}).map(([name, value]) => ({
          name: name.replace('Speed', '').trim(),
          value
        })).sort((a, b) => b.value - a.value);

        setAnalyticsData({
          dataQuality: stats.dataQuality || null,
          totalRevenue: stats.totalRevenue || 0,
          itServicesRevenue: stats.itServicesRevenue || 0,
          combinedRevenue: stats.combinedRevenue || 0, // NEW: Total Incoming
          vendorPayouts: stats.vendorPayouts || 0, // NEW: Total Outgoing
          standingAmount: stats.standingAmount || 0, // NEW: Net
          newCustomers: stats.total || 0,
          ddSuccessRate: stats.ddSuccessRate || 0,
          activeServices: stats.activeServices || 0,
          revenueGrowth: revenueGrowth,
          customerGrowth: customerGrowth,
          ddGrowth: ddGrowth,
          servicesGrowth: servicesGrowth,
          history: stats.history || [],
          itServicesHistory: stats.itServicesHistory || [],
          planDistribution: planData,
          recentActivity: stats.recentActivity || [],
          // GST / BAS — pre-computed totals + monthly maps. Admin uses these
          // to fill BAS quarterly. Numbers reflect realized cash (paid only).
          gstCollected: stats.gstCollected || 0,                         // BAS 1A
          gstPaid: stats.gstPaid || 0,                                   // BAS 1B
          subtotalExGst: stats.subtotalExGst || 0,                       // Sales ex-GST
          vendorSubtotalExGst: stats.vendorSubtotalExGst || 0,           // Purchases ex-GST
          gstCollectedByMonth: stats.gstCollectedByMonth || {},
          gstPaidByMonth: stats.gstPaidByMonth || {},
          vendorInvoicesMissingTax: stats.vendorInvoicesMissingTax || 0,
          vendorInvoicesPendingExtraction: stats.vendorInvoicesPendingExtraction || 0,
        });
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#282828] border border-[#504945] p-3 rounded shadow-lg">
          <p className="text-[#ebdbb2] font-medium mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.name.includes('Revenue') ? '$' : ''}{entry.value.toLocaleString()}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Merge history for charting
  const chartData = analyticsData.history.map(item => {
    const itItem = analyticsData.itServicesHistory.find(it => it.date === item.date);
    return {
      ...item,
      itRevenue: itItem ? itItem.revenue : 0
    };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#fabd2f]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[#ebdbb2]">Analytics & Reports</h1>
          <p className="text-[#bdae93]">Comprehensive insights into Peaknet performance</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={loadAnalytics}
            className="p-2 text-[#bdae93] hover:text-[#ebdbb2] transition-colors"
            title="Refresh Data"
          >
            <FaRedo />
          </button>
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fabd2f] focus:border-transparent"
          >
            <option value="1y">Last Year</option>
          </select>
          <button className="px-4 py-2 bg-[#fabd2f] text-[#1d2021] rounded-lg hover:bg-[#fe8019] transition-colors font-medium flex items-center">
            <FaDownload className="mr-2" />
            Export Report
          </button>
        </div>
      </div>

      <DataQualityBanner dataQuality={analyticsData.dataQuality} />

      {/* Financial Overview - Row 1 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Total Incoming */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Total Incoming</p>
              <p className="text-2xl font-bold text-[#b8bb26]">${analyticsData.combinedRevenue.toLocaleString()}</p>
              <p className="text-xs text-[#928374]">ISP + IT Services</p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaDollarSign className="text-[#b8bb26]" size={20} />
            </div>
          </div>
        </div>

        {/* Vendor Payouts (Outgoing) */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Vendor Payouts</p>
              <p className="text-2xl font-bold text-[#fb4934]">${analyticsData.vendorPayouts.toLocaleString()}</p>
              <p className="text-xs text-[#928374]">Total Outgoing</p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaCreditCard className="text-[#fb4934]" size={20} />
            </div>
          </div>
        </div>

        {/* Standing Amount (Net) */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Standing Amount</p>
              <p className="text-2xl font-bold text-[#83a598]">${analyticsData.standingAmount.toLocaleString()}</p>
              <p className="text-xs text-[#928374]">Global Net Profit</p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaChartLine className="text-[#83a598]" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* GST / BAS Summary — admin reads these for quarterly BAS lodgement.
          All numbers are realized cash (paid invoices only). Net GST = 1A - 1B. */}
      <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-[#ebdbb2]">GST / BAS Summary</h2>
            <p className="text-sm text-[#928374]">Realized GST across all paid invoices. Use these to fill quarterly BAS.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* GST Collected — BAS 1A */}
          <div className="bg-[#3c3836] border border-[#458588] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase font-semibold text-[#83a598]">BAS 1A · GST Collected</p>
              <span className="text-xs font-mono text-[#928374]">/11</span>
            </div>
            <p className="text-2xl font-bold text-[#b8bb26]">${analyticsData.gstCollected.toFixed(2)}</p>
            <p className="text-xs text-[#928374] mt-1">GST from customer invoices</p>
          </div>
          {/* GST Paid — BAS 1B */}
          <div className="bg-[#3c3836] border border-[#d79921] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase font-semibold text-[#fabd2f]">BAS 1B · GST Paid</p>
              <span className="text-xs font-mono text-[#928374]">credit</span>
            </div>
            <p className="text-2xl font-bold text-[#fabd2f]">${analyticsData.gstPaid.toFixed(2)}</p>
            <p className="text-xs text-[#928374] mt-1">GST on vendor invoices</p>
          </div>
          {/* Net GST */}
          <div className="bg-[#3c3836] border border-[#cc241d] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase font-semibold text-[#fb4934]">Net GST Payable</p>
              <span className="text-xs font-mono text-[#928374]">1A − 1B</span>
            </div>
            <p className={`text-2xl font-bold ${(analyticsData.gstCollected - analyticsData.gstPaid) >= 0 ? 'text-[#fb4934]' : 'text-[#b8bb26]'}`}>
              ${(analyticsData.gstCollected - analyticsData.gstPaid).toFixed(2)}
            </p>
            <p className="text-xs text-[#928374] mt-1">
              {(analyticsData.gstCollected - analyticsData.gstPaid) >= 0 ? 'Owed to ATO' : 'Refundable from ATO'}
            </p>
          </div>
          {/* Sales ex-GST */}
          <div className="bg-[#3c3836] border border-[#504945] rounded-lg p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs uppercase font-semibold text-[#bdae93]">Sales (ex-GST)</p>
              <span className="text-xs font-mono text-[#928374]">subtotal</span>
            </div>
            <p className="text-2xl font-bold text-[#ebdbb2]">${analyticsData.subtotalExGst.toFixed(2)}</p>
            <p className="text-xs text-[#928374] mt-1">For income reporting</p>
          </div>
        </div>

        {/* GST collected from customers (all-time) — sub-tile for context.
            Quarter-scoped view + lodgement history lives in BasQuarterPanel below. */}
        <p className="text-xs text-[#928374] mt-3">
            Numbers above are all-time totals across every paid invoice. For BAS lodgement you'll want the per-quarter view in the BAS Quarters section below.
        </p>

        {/* Data-quality warnings if vendor side has gaps */}
        {(analyticsData.vendorInvoicesMissingTax > 0 || analyticsData.vendorInvoicesPendingExtraction > 0) && (
          <div className="mt-4 p-3 bg-[#3c3320] border border-[#d79921] rounded-lg text-sm text-[#fbf1c7]">
            <p className="font-medium text-[#fabd2f] mb-1">⚠ Vendor GST may be understated</p>
            <ul className="list-disc list-inside space-y-0.5 text-xs">
              {analyticsData.vendorInvoicesMissingTax > 0 && (
                <li>{analyticsData.vendorInvoicesMissingTax} paid vendor invoice(s) have no <code>tax</code> value recorded — their GST credit is missing from 1B.</li>
              )}
              {analyticsData.vendorInvoicesPendingExtraction > 0 && (
                <li>{analyticsData.vendorInvoicesPendingExtraction} vendor invoice(s) still <code>PENDING</code> extraction — manually fill subtotal/tax in the Vendor Invoices tab.</li>
              )}
            </ul>
          </div>
        )}
      </div>

      {/* BAS Quarters — per-quarter scoped view + lodgement history */}
      <BasQuarterPanel
        gstCollectedByMonth={analyticsData.gstCollectedByMonth}
        gstPaidByMonth={analyticsData.gstPaidByMonth}
      />

      {/* Net Profit Breakdown - Row 2 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">ISP Net Profit</p>
              <p className="text-2xl font-bold text-[#fabd2f]">${(analyticsData.ispNetProfit || 0).toLocaleString()}</p>
              <p className="text-xs text-[#928374]">Revenue - Vendor Payouts</p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaWifi className="text-[#fabd2f]" size={20} />
            </div>
          </div>
        </div>

        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">IT Net Profit</p>
              <p className="text-2xl font-bold text-[#458588]">${(analyticsData.itNetProfit || 0).toLocaleString()}</p>
              <p className="text-xs text-[#928374]">Service Revenue</p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaChartLine className="text-[#458588]" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Breakdown & Services - Row 3 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* ISP Revenue */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">ISP Revenue</p>
              <p className="text-2xl font-bold text-[#ebdbb2]">${analyticsData.totalRevenue.toLocaleString()}</p>
              <p className={`text-xs ${analyticsData.revenueGrowth > 0 ? 'text-[#b8bb26]' : analyticsData.revenueGrowth < 0 ? 'text-[#fb4934]' : 'text-[#928374]'}`}>
                {analyticsData.revenueGrowth > 0 ? '+' : ''}{analyticsData.revenueGrowth}% from last month
              </p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaDollarSign className="text-[#fabd2f]" size={20} />
            </div>
          </div>
        </div>

        {/* IT Services Revenue */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">IT Services</p>
              <p className="text-2xl font-bold text-[#ebdbb2]">${analyticsData.itServicesRevenue.toLocaleString()}</p>
              <p className="text-xs text-[#83a598]">Project Revenue</p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaDollarSign className="text-[#458588]" size={20} />
            </div>
          </div>
        </div>

        {/* Total Customers */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Total Customers</p>
              <p className="text-2xl font-bold text-[#ebdbb2]">{analyticsData.newCustomers}</p>
              <p className={`text-xs ${analyticsData.customerGrowth > 0 ? 'text-[#b8bb26]' : analyticsData.customerGrowth < 0 ? 'text-[#fb4934]' : 'text-[#928374]'}`}>
                {analyticsData.customerGrowth > 0 ? '+' : ''}{analyticsData.customerGrowth}% from last month
              </p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaUsers className="text-[#83a598]" size={20} />
            </div>
          </div>
        </div>

        {/* Active Services */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-[#bdae93]">Active Services</p>
              <p className="text-2xl font-bold text-[#ebdbb2]">{analyticsData.activeServices}</p>
              <p className={`text-xs ${analyticsData.servicesGrowth > 0 ? 'text-[#b8bb26]' : analyticsData.servicesGrowth < 0 ? 'text-[#fb4934]' : 'text-[#928374]'}`}>
                {analyticsData.servicesGrowth > 0 ? '+' : ''}{analyticsData.servicesGrowth}% from last month
              </p>
            </div>
            <div className="w-10 h-10 bg-[#3c3836] rounded-lg flex items-center justify-center">
              <FaWifi className="text-[#fe8019]" size={20} />
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Revenue Trend</h3>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-[#fabd2f] rounded-full mr-2"></div>
                <span className="text-xs text-[#bdae93]">ISP</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-[#458588] rounded-full mr-2"></div>
                <span className="text-xs text-[#bdae93]">IT Services</span>
              </div>
            </div>
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#fabd2f" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#fabd2f" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorITRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#458588" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#458588" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#3c3836" vertical={false} />
                <XAxis dataKey="name" stroke="#928374" tick={{ fontSize: 12 }} />
                <YAxis stroke="#928374" tick={{ fontSize: 12 }} tickFormatter={(val) => `$${val}`} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  name="ISP Revenue"
                  stroke="#fabd2f"
                  fillOpacity={0.6}
                  fill="url(#colorRevenue)"
                  stackId="1"
                />
                <Area
                  type="monotone"
                  dataKey="itRevenue"
                  name="IT Revenue"
                  stroke="#458588"
                  fillOpacity={0.6}
                  fill="url(#colorITRevenue)"
                  stackId="1"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Customer Growth */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Customer Growth</h3>
            <FaChartBar className="text-[#928374]" size={20} />
          </div>
          <div className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analyticsData.history}>
                <CartesianGrid strokeDasharray="3 3" stroke="#3c3836" vertical={false} />
                <XAxis dataKey="name" stroke="#928374" tick={{ fontSize: 12 }} />
                <YAxis stroke="#928374" tick={{ fontSize: 12 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="totalCustomers" name="Total Customers" fill="#83a598" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Plan Distribution */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6 lg:col-span-1">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Plan Distribution</h3>
            <FaChartPie className="text-[#928374]" size={20} />
          </div>
          <div className="h-64 w-full flex items-center justify-center">
            {analyticsData.planDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={analyticsData.planDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {analyticsData.planDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-[#928374]">No plan data available</p>
            )}
          </div>
          <div className="mt-4 space-y-3">
            {analyticsData.planDistribution.map((entry, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: COLORS[index % COLORS.length] }}></div>
                  <span className="text-[#bdae93]">{entry.name}</span>
                </div>
                <span className="text-[#ebdbb2] font-medium">{entry.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-[#282828] rounded-xl shadow-sm border border-[#3c3836] p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-[#ebdbb2]">Recent Activity</h3>
            <FaCalendar className="text-[#928374]" size={20} />
          </div>
          <div className="space-y-4">
            {analyticsData.recentActivity.length > 0 ? (
              analyticsData.recentActivity.map((activity, index) => (
                <div key={index} className="flex items-center space-x-4 p-4 bg-[#3c3836] rounded-lg">
                  <div className="w-8 h-8 bg-[#282828] border border-[#458588] rounded-full flex items-center justify-center">
                    <FaUsers className="text-[#b8bb26]" size={14} />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-[#ebdbb2]">{activity.description}</p>
                    <p className="text-xs text-[#928374]">
                      Monthly Fee: ${activity.amount}
                    </p>
                  </div>
                  <span className="text-xs text-[#928374]">
                    {new Date(activity.date).toLocaleDateString()}
                  </span>
                </div>
              ))
            ) : (
              <p className="text-[#928374] text-center py-4">No recent activity found</p>
            )}

            {/* Hardcoded sample for demo if empty (fallback) */}
            {analyticsData.recentActivity.length === 0 && (
              <div className="text-xs text-center text-[#504945] mt-2">
                Displaying sample data as no live activity found...
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics; 